import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { GoldButton } from '@/components/common/GoldButton';
import { useAuth } from '@/context/AuthContext';
import { useApiErrorToast } from '@/hooks/useApiErrorToast';
import { t } from '@/i18n';
import {
  canonicalizeEmailForSubmit,
  getEmailValidationError,
  isSupportedEmail,
  MAX_EMAIL_LENGTH,
} from '@/utils/authValidation';
import styles from './VerifyEmailPage.module.css';

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [touched, setTouched] = useState(false);
  const { requestPasswordReset } = useAuth();
  const onApiError = useApiErrorToast();
  const navigate = useNavigate();

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!isSupportedEmail(email) || submitting) return;
    setSubmitting(true);
    try {
      const submittedEmail = canonicalizeEmailForSubmit(email);
      await requestPasswordReset(submittedEmail);
      navigate(`/reset-password?email=${encodeURIComponent(submittedEmail)}`, {
        replace: true,
      });
    } catch (err) {
      onApiError(err, t.auth.forgot.sendFailed);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.icon}>🔑</div>
        <div className={styles.title}>{t.auth.forgot.title}</div>
        <div className={styles.sub}>{t.auth.forgot.sub}</div>
        <form onSubmit={submit} className={styles.resendForm} noValidate>
          <input
            className={styles.inp}
            type="email"
            placeholder={t.common.emailPlaceholder}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={() => setTouched(true)}
            disabled={submitting}
            autoComplete="email"
            autoCapitalize="none"
            inputMode="email"
            spellCheck={false}
            maxLength={MAX_EMAIL_LENGTH}
            required
          />
          {touched && !isSupportedEmail(email) && (
            <div style={{ fontSize: 11, color: '#e53e3e', marginTop: -6 }}>
              {getEmailValidationError(email) === 'internationalLocalPart'
                ? t.common.emailInternationalUnsupported
                : t.common.emailInvalid}
            </div>
          )}
          <GoldButton type="submit" disabled={!email || submitting}>
            {submitting ? t.common.sending : t.auth.forgot.submit}
          </GoldButton>
        </form>
        <Link to="/login" className={styles.backLink}>
          {t.common.backToLogin}
        </Link>
      </div>
    </div>
  );
}
